import { ArrowType, LineType } from '@ardoq/api-types';
import { TAGS_FIELD_NAME } from '@ardoq/data-model';
import { modelOperations } from '@ardoq/core';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

type ArrowTypeLabelEntry = { key: string; defaultText: string };

const arrowTypeLabelEntries = new Map<ArrowType, ArrowTypeLabelEntry>([
  [ArrowType.NONE, { key: 'arrowType.none', defaultText: 'None' }],
  [ArrowType.BOTH, { key: 'arrowType.both', defaultText: 'Both' }],
  [ArrowType.BOTH_START, { key: 'arrowType.both', defaultText: 'Both' }],
  [ArrowType.DIAMOND, { key: 'arrowType.diamond', defaultText: 'Diamond' }],
  [
    ArrowType.DIAMOND_START,
    { key: 'arrowType.diamond', defaultText: 'Diamond' },
  ],
  [
    ArrowType.DIAMOND_FILLED,
    { key: 'arrowType.diamondFilled', defaultText: 'Diamond Filled' },
  ],
  [
    ArrowType.DIAMOND_FILLED_START,
    { key: 'arrowType.diamondFilled', defaultText: 'Diamond Filled' },
  ],
  [ArrowType.TOP, { key: 'arrowType.top', defaultText: 'Top' }],
  [ArrowType.TOP_START, { key: 'arrowType.top', defaultText: 'Top' }],
  [ArrowType.BOTTOM, { key: 'arrowType.bottom', defaultText: 'Bottom' }],
  [ArrowType.BOTTOM_START, { key: 'arrowType.bottom', defaultText: 'Bottom' }],
  [
    ArrowType.BOTH_FILLED,
    { key: 'arrowType.bothFilled', defaultText: 'Both Filled' },
  ],
  [
    ArrowType.BOTH_FILLED_START,
    { key: 'arrowType.bothFilled', defaultText: 'Both Filled' },
  ],
  [
    ArrowType.BOTH_OUTLINED,
    { key: 'arrowType.bothOutlined', defaultText: 'Both Outlined' },
  ],
  [
    ArrowType.BOTH_OUTLINED_START,
    { key: 'arrowType.bothOutlined', defaultText: 'Both Outlined' },
  ],
  [ArrowType.CIRCLE, { key: 'arrowType.circle', defaultText: 'Circle' }],
  [ArrowType.CIRCLE_START, { key: 'arrowType.circle', defaultText: 'Circle' }],
  [ArrowType.ONE, { key: 'arrowType.one', defaultText: 'One' }],
  [ArrowType.ONE_START, { key: 'arrowType.one', defaultText: 'One' }],
  [
    ArrowType.ONE_MANDATORY,
    { key: 'arrowType.oneMandatory', defaultText: 'One Mandatory' },
  ],
  [
    ArrowType.ONE_MANDATORY_START,
    { key: 'arrowType.oneMandatory', defaultText: 'One Mandatory' },
  ],
  [ArrowType.MANY, { key: 'arrowType.many', defaultText: 'Many' }],
  [ArrowType.MANY_START, { key: 'arrowType.many', defaultText: 'Many' }],
  [
    ArrowType.ONE_OR_MANY,
    { key: 'arrowType.oneOrMany', defaultText: 'One Or Many' },
  ],
  [
    ArrowType.ONE_OR_MANY_START,
    { key: 'arrowType.oneOrMany', defaultText: 'One Or Many' },
  ],
  [
    ArrowType.ZERO_OR_ONE,
    { key: 'arrowType.zeroOrOne', defaultText: 'Zero Or One' },
  ],
  [
    ArrowType.ZERO_OR_ONE_START,
    { key: 'arrowType.zeroOrOne', defaultText: 'Zero Or One' },
  ],
  [
    ArrowType.ZERO_OR_MANY,
    { key: 'arrowType.zeroOrMany', defaultText: 'Zero Or Many' },
  ],
  [
    ArrowType.ZERO_OR_MANY_START,
    { key: 'arrowType.zeroOrMany', defaultText: 'Zero Or Many' },
  ],
  [ArrowType.DEFAULT, { key: 'arrowType.default', defaultText: 'Default' }],
  [
    ArrowType.DEFAULT_START,
    { key: 'arrowType.default', defaultText: 'Default' },
  ],
]);

const translateArrowEntry = (
  entry: ArrowTypeLabelEntry,
  i18nLocale: I18nLocale
): string => t(i18nLocale, `renderers:${entry.key}`, entry.defaultText);

export const arrowTypeLabelMap = new Map(
  Array.from(arrowTypeLabelEntries.entries()).map(([arrowType, entry]) => [
    arrowType,
    entry.defaultText,
  ])
);

export const getArrowTypeLabel = (
  arrowType: ArrowType,
  i18nLocale: I18nLocale = DEFAULT_ARDOQ_I18NLOCALE
): string => {
  const entry = arrowTypeLabelEntries.get(arrowType);
  if (!entry) return arrowType as string;
  return translateArrowEntry(entry, i18nLocale);
};

const lineTypeLabelEntries = new Map<LineType, ArrowTypeLabelEntry>([
  [LineType.SOLID, { key: 'lineType.solid', defaultText: 'Solid' }],
  [LineType.DOTTED, { key: 'lineType.dotted', defaultText: 'Dotted' }],
  [LineType.DASHED, { key: 'lineType.dashed', defaultText: 'Dashed' }],
]);

export const getLineTypeLabel = (
  lineType: LineType,
  i18nLocale: I18nLocale = DEFAULT_ARDOQ_I18NLOCALE
): string => {
  const entry = lineTypeLabelEntries.get(lineType);
  if (!entry) return lineType as string;
  return translateArrowEntry(entry, i18nLocale);
};

export const getReferenceTypeSvgStyle =
  modelOperations.getReferenceTypeSvgStyle;

/** Map from reserved attribute name to its translation entry */
const DEFAULT_ATTRIBUTE_NAME_LABEL_ENTRIES = new Map<
  string,
  ArrowTypeLabelEntry
>([
  ['_id', { key: 'attribute.id', defaultText: 'ID' }],
  ['_order', { key: 'attribute.order', defaultText: 'Order' }],
  ['_version', { key: 'attribute.version', defaultText: 'Version' }],
  [
    'child-count',
    { key: 'attribute.childCount', defaultText: 'Number of children' },
  ],
  ['children', { key: 'attribute.children', defaultText: 'Children' }],
  ['color', { key: 'attribute.color', defaultText: 'Color' }],
  // 'component-key' is distributed on API side on many places
  // so for now we are not transforming it to `componentKey`
  ['component-key', { key: 'attribute.ardoqId', defaultText: 'Ardoq ID' }],
  ['created-by', { key: 'attribute.createdBy', defaultText: 'Created by' }],
  ['created', { key: 'attribute.created', defaultText: 'Created' }],
  [
    'createdByEmail',
    { key: 'attribute.createdByEmail', defaultText: 'Created by (email)' },
  ],
  [
    'createdByName',
    { key: 'attribute.createdByName', defaultText: 'Created by (name)' },
  ],
  [
    'createdProvenance',
    { key: 'attribute.createdOrigin', defaultText: 'Origin' },
  ],
  ['description', { key: 'attribute.description', defaultText: 'Description' }],
  [
    'displayText',
    { key: 'attribute.displayText', defaultText: 'Display text' },
  ],
  ['icon', { key: 'attribute.icon', defaultText: 'Icon' }],
  ['id', { key: 'attribute.id', defaultText: 'ID' }],
  ['image', { key: 'attribute.image', defaultText: 'Image' }],
  ['isPublic', { key: 'attribute.isPublic', defaultText: 'Is public' }],
  ['label', { key: 'attribute.label', defaultText: 'Label' }],
  [
    'last-modified-by',
    { key: 'attribute.updatedBy', defaultText: 'Updated by' },
  ],
  ['lastModifiedBy', { key: 'attribute.updatedBy', defaultText: 'Updated by' }],
  [
    'lastModifiedByEmail',
    { key: 'attribute.updatedByEmail', defaultText: 'Updated by (email)' },
  ],
  [
    'lastModifiedByName',
    { key: 'attribute.updatedByName', defaultText: 'Updated by (name)' },
  ],
  [
    'lastProvenance',
    { key: 'attribute.updatedVia', defaultText: 'Updated via' },
  ],
  [
    'lastUpdated',
    { key: 'attribute.lastUpdated', defaultText: 'Last updated' },
  ],
  ['locked', { key: 'attribute.locked', defaultText: 'Locked' }],
  ['lock', { key: 'attribute.locked', defaultText: 'Locked' }],
  ['model', { key: 'attribute.model', defaultText: 'Model' }],
  ['name', { key: 'attribute.name', defaultText: 'Name' }],
  [
    'numberFormatOptions',
    { key: 'attribute.numberFormat', defaultText: 'Number format' },
  ],
  ['order', { key: 'attribute.order', defaultText: 'Order' }],
  ['origin', { key: 'attribute.origin', defaultText: 'Origin' }],
  ['parent', { key: 'attribute.parent', defaultText: 'Parent' }],
  ['path', { key: 'attribute.path', defaultText: 'Path' }],
  [
    'refDescription',
    { key: 'attribute.description', defaultText: 'Description' },
  ],
  [
    'refDisplayText',
    { key: 'attribute.displayText', defaultText: 'Display text' },
  ],
  ['refOrder', { key: 'attribute.order', defaultText: 'Order' }],
  ['refType', { key: 'attribute.type', defaultText: 'Type' }],
  [
    'returnValue',
    { key: 'attribute.returnValue', defaultText: 'Return value' },
  ],
  ['rootWorkspace', { key: 'attribute.workspace', defaultText: 'Workspace' }],
  ['shape', { key: 'attribute.shape', defaultText: 'Shape' }],
  ['source', { key: 'attribute.source', defaultText: 'Source' }],
  [
    'sourceCardinality',
    { key: 'attribute.sourceCardinality', defaultText: 'Source cardinality' },
  ],
  ['srcName', { key: 'attribute.name', defaultText: 'Name' }],
  ['srcParent', { key: 'attribute.parent', defaultText: 'Parent' }],
  ['srcType', { key: 'attribute.type', defaultText: 'Type' }],
  [TAGS_FIELD_NAME, { key: 'attribute.tags', defaultText: 'Tags' }],
  ['target', { key: 'attribute.target', defaultText: 'Target' }],
  [
    'targetCardinality',
    { key: 'attribute.targetCardinality', defaultText: 'Target cardinality' },
  ],
  ['targetName', { key: 'attribute.name', defaultText: 'Name' }],
  ['targetType', { key: 'attribute.type', defaultText: 'Type' }],
  ['targetParent', { key: 'attribute.parent', defaultText: 'Parent' }],
  [
    'targetWorkspace',
    { key: 'attribute.targetWorkspace', defaultText: 'Target workspace' },
  ],
  ['type', { key: 'attribute.type', defaultText: 'Type' }],
  ['typeId', { key: 'attribute.typeId', defaultText: 'Type ID' }],
  ['typeName', { key: 'attribute.typeName', defaultText: 'Type name' }],
  ['version', { key: 'attribute.version', defaultText: 'Version' }],
  ['workspace', { key: 'attribute.workspace', defaultText: 'Workspace' }],
  [
    'componentType',
    { key: 'attribute.componentTypes', defaultText: 'Component types' },
  ],
  [
    'referenceType',
    { key: 'attribute.referenceTypes', defaultText: 'Reference types' },
  ],
  [
    'global',
    {
      key: 'attribute.appliesToAllComponentTypes',
      defaultText: 'Applies to all component types',
    },
  ],
  [
    'globalref',
    {
      key: 'attribute.appliesToAllReferenceTypes',
      defaultText: 'Applies to all reference types',
    },
  ],
  [
    'defaultValue',
    { key: 'attribute.defaultValue', defaultText: 'Default value' },
  ],

  // for APIEntityType.COMPONENT_TYPE
  [
    'returnsValue',
    { key: 'attribute.returnValue', defaultText: 'Return value' },
  ],
  ['standard', { key: 'attribute.standard', defaultText: 'Standard' }],
  ['level', { key: 'attribute.level', defaultText: 'Level' }],
  ['index', { key: 'attribute.index', defaultText: 'Index' }],

  // for APIEntityType.REFERENCE_TYPE
  ['svgStyle', { key: 'attribute.svgStyle', defaultText: 'SVG style' }],
  ['lineEnding', { key: 'attribute.lineEnding', defaultText: 'Line ending' }],
  [
    'lineBeginning',
    { key: 'attribute.lineBeginning', defaultText: 'Line beginning' },
  ],
  ['line', { key: 'attribute.line', defaultText: 'Line' }],
  [
    'hasCardinality',
    { key: 'attribute.hasCardinality', defaultText: 'Has cardinality' },
  ],

  // for APIEntityType.MODEL
  ['flexible', { key: 'attribute.flexible', defaultText: 'Is flexible' }],

  // for APIEntityType.WORKSPACE
  ['startView', { key: 'attribute.startView', defaultText: 'Start view' }],
  [
    'defaultPerspective',
    {
      key: 'attribute.defaultPerspective',
      defaultText: 'Default perspective',
    },
  ],
  [
    'defaultSort',
    { key: 'attribute.defaultSort', defaultText: 'Default sort' },
  ],

  // for APIEntityType.Tag
  ['components', { key: 'attribute.components', defaultText: 'Components' }],
  ['references', { key: 'attribute.references', defaultText: 'References' }],

  // pretending fields have stored the options for list types as listOptions
  ['listOptions', { key: 'attribute.options', defaultText: 'Options' }],

  // for LocallyDerivedFieldSettings transformations
  [
    'calculatedFieldSettings',
    { key: 'attribute.transformations', defaultText: 'Transformations' },
  ],
]);

export const getDefaultAttributeLabel = (
  attributeName: string,
  i18nLocale: I18nLocale = DEFAULT_ARDOQ_I18NLOCALE
): string | undefined => {
  const entry = DEFAULT_ATTRIBUTE_NAME_LABEL_ENTRIES.get(attributeName);
  if (!entry) return undefined;
  return translateArrowEntry(entry, i18nLocale);
};

export const getDefaultAttributeLabelForComponent = (
  attributeName: string,
  i18nLocale: I18nLocale = DEFAULT_ARDOQ_I18NLOCALE
): string | undefined => {
  if (attributeName === 'type' || attributeName === 'typeId') {
    return t(i18nLocale, 'renderers:attribute.componentType', 'Component type');
  }
  return getDefaultAttributeLabel(attributeName, i18nLocale);
};

export const getDefaultAttributeLabelForReference = (
  attributeName: string,
  i18nLocale: I18nLocale = DEFAULT_ARDOQ_I18NLOCALE
): string | undefined => {
  if (attributeName === 'type') {
    return t(i18nLocale, 'renderers:attribute.referenceType', 'Reference type');
  }
  return getDefaultAttributeLabel(attributeName, i18nLocale);
};

export const getAllDefaultAttributeNames = (): string[] => {
  return Array.from(DEFAULT_ATTRIBUTE_NAME_LABEL_ENTRIES.keys());
};

export const getAllDefaultAttributes = (
  i18nLocale: I18nLocale = DEFAULT_ARDOQ_I18NLOCALE
): Array<{
  name: string;
  label: string;
}> => {
  return Array.from(DEFAULT_ATTRIBUTE_NAME_LABEL_ENTRIES.entries()).map(
    ([name, entry]) => ({ name, label: translateArrowEntry(entry, i18nLocale) })
  );
};
